import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { waPhone, formatPhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { WhatsappLogo, CaretDown, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { STAGES } from "./DueStageCards";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const dt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "");
const KIND = { gate: "Gate", wholesale: "Wholesale", order: "Order" };

export function buildMessage(c, business) {
  const bills = c.pending_orders.map((o) => `• ${o.order_number} (${dt(o.created_at)}) – ${inr(o.remaining)}`).join("\n");
  return `Namaste ${c.customer_name || "ji"},\n\n${business?.name || "Shobhit Creation"} ki taraf se yaad dilana tha ki aapka ${inr(c.balance_due)} baaki hai (sabse purana bill ${c.days} din pehle ka).\n\n${bills}\n\nKripya jaldi payment kar dein.${business?.phone ? `\nSampark: ${business.phone}` : ""}\n\nDhanyavaad 🙏`;
}

export function DueCustomerRow({ c, business, onSent }) {
  const [open, setOpen] = useState(false);
  const red = c.overdue_month;
  const stage = STAGES.find((s) => s.key === c.stage);

  const send = async () => {
    const text = buildMessage(c, business);
    window.open(`https://wa.me/${waPhone(c.mobile)}?text=${encodeURIComponent(text)}`, "_blank");
    try {
      await api.post("/dues/reminders", { mobile: c.mobile, amount: c.balance_due, days: c.days, message: text });
      toast.success("Reminder record ho gaya");
      onSent();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reminder record nahi hua");
    }
  };

  return (
    <div data-testid={`due-row-${c.mobile}`}
      className={`rounded-sm border ${red ? "border-red-500/60 bg-red-500/10 border-l-4 border-l-red-500" : "border-slate-700 bg-slate-800/30"}`}>
      <div className="flex flex-wrap items-center gap-3 p-3">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-[180px] text-left" data-testid={`due-row-toggle-${c.mobile}`}>
          <div className="flex items-center gap-2">
            <CaretDown size={12} className={`text-slate-500 transition-transform ${open ? "" : "-rotate-90"}`} />
            <span className={`font-semibold truncate ${red ? "text-red-200" : "text-slate-100"}`}>{c.customer_name || "—"}</span>
            <span data-testid={`due-stage-badge-${c.mobile}`}
              className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold ${red ? "bg-red-600 text-white" : "bg-slate-700 text-slate-200"}`}>
              {stage?.label}
            </span>
          </div>
          <div className="text-[11px] text-slate-400 font-mono-num pl-5">
            {formatPhone(c.mobile)} · {c.pending_orders.length} bill · {c.days} din purana
          </div>
          {c.last_reminder && (
            <div className="text-[11px] text-emerald-300/80 pl-5 flex items-center gap-1" data-testid={`due-last-reminder-${c.mobile}`}>
              <ClockCounterClockwise size={11} /> Last reminder {dt(c.last_reminder.at)} · {c.last_reminder.by} · total {c.last_reminder.count}
            </div>
          )}
        </button>
        <div className={`font-mono-num text-lg font-bold ${red ? "text-red-400" : "text-slate-100"}`} data-testid={`due-amount-${c.mobile}`}>
          {inr(c.balance_due)}
        </div>
        <Button size="sm" onClick={send} className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm" data-testid={`due-remind-btn-${c.mobile}`}>
          <WhatsappLogo size={16} weight="fill" className="mr-1" /> Reminder
        </Button>
      </div>
      {open && (
        <div className="border-t border-slate-700/70 px-3 py-2 space-y-1" data-testid={`due-bills-${c.mobile}`}>
          {c.pending_orders.map((o) => (
            <div key={o.order_id} className="flex justify-between gap-3 text-xs font-mono-num">
              <span className={`min-w-0 ${o.days > 30 ? "text-red-300" : "text-slate-300"}`}>
                {o.order_number} · {KIND[o.kind]} · {dt(o.created_at)} · {o.days} din
              </span>
              <span className={`shrink-0 ${o.days > 30 ? "text-red-400" : "text-slate-200"}`}>{inr(o.remaining)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
